// src/context/FAQContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { BASE_URL } from "../config/apiconfig";
import { useAuth } from "./AuthContext";

const FAQContext = createContext();

export function FAQProvider({ children }) {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { token: authToken, adminId, isReady } = useAuth();

  const getToken = useCallback(() => {
    return authToken || localStorage.getItem("token") || null;
  }, [authToken]);

  const getHeaders = useCallback(() => {
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };
    const t = getToken();
    if (t) headers.Authorization = `Bearer ${t}`;
    return headers;
  }, [getToken]);


  const normalizeItems = (items = []) =>
    items.map((item) => ({
      id: Number(item.id ?? item.ID ?? item.faq_id ?? Date.now()),
      question: item.question ?? item.title ?? "",
      answer: item.answer ?? item.body ?? "",
      category: item.category ?? "General",
      status: item.status ?? "Active",
      priority: item.priority ?? "medium",
      views: Number(item.views ?? 0),
      helpful: Number(item.helpful ?? 0),
      createdAt: item.created_at ?? item.createdAt ?? new Date().toISOString(),
      updatedAt: item.updated_at ?? item.updatedAt ?? new Date().toISOString(),
    }));

    // Fetch FAQs
    const fetchFaqs = useCallback(
      async (opts = {}) => {
        if (!isReady) return; 
        setLoading(true);
        setError(null);

      try {
        const res = await fetch(`${BASE_URL}/faqs`, {
            method: "GET",
            headers: getHeaders(),
            signal: opts.signal,
          });

          const text = await res.text().catch(() => null);

            let data = null;
            try {
              data = text ? JSON.parse(text) : null;
            } catch (e) {
              data = null;
            }

            if (!res.ok) {
              const msg = (data && (data.message || JSON.stringify(data))) || text || `HTTP ${res.status}`;
              throw new Error(msg);
            }

            const items = Array.isArray(data) ? data : data?.data ?? [];
            const parsed = normalizeItems(items);
            setFaqs(parsed);
            return parsed;
          } catch (err) {
            if (err.name === "AbortError") {
              console.info("[FAQ] fetch aborted");
              return;
            }
            console.error("[FAQ] fetchFaqs error:", err);
            setError(err.message || "Failed to fetch FAQs");
            throw err;
          } finally {
            setLoading(false);
          }
        },
        [getHeaders, isReady]
      );

  // Create FAQ
  const createFaq = useCallback(
    async (payload) => {
      setLoading(true);
      setError(null);

      try {
        const createAuditId = localStorage.getItem("admin_id");
        const payloadWithAudit = {
          ...payload,
          create_audit_id: createAuditId 
        };

        const res = await fetch(`${BASE_URL}/faqs`, {
          method: "POST",
          headers: getHeaders(),
          body: JSON.stringify(payloadWithAudit),
        });

        const data = await res.json().catch(() => null);

        if (!res.ok) {
          const msg = data?.message || JSON.stringify(data) || `HTTP ${res.status}`;
          throw new Error(msg);
        }

        const created = data?.data ?? data;
        if (created) {
          const item = normalizeItems([created])[0];
          setFaqs((prev) => [item, ...prev]);
          return item;
        }
        await fetchFaqs();
        return true;
      } catch (err) {
        console.error("[FAQ] createFaq error:", err);
        setError(err.message || "Failed to create FAQ");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [getHeaders, fetchFaqs]
  );

  // Update FAQ
  const updateFaq = useCallback(
    async (id, payload) => {
      setLoading(true);
      setError(null);

      try {
        const updateAuditId = localStorage.getItem("admin_id"); // replace with your key
        const payloadWithAudit = {
          ...payload,
          update_audit_id: updateAuditId
        };

        const res = await fetch(`${BASE_URL}/faqs/${id}`, {
          method: "PUT",
          headers: getHeaders(),
          body: JSON.stringify(payloadWithAudit),
        });

        const data = await res.json().catch(() => null);

        if (!res.ok) {
          const msg = data?.message || JSON.stringify(data) || `HTTP ${res.status}`;
          throw new Error(msg);
        }

        const updated = data?.data ?? data;
        if (updated) {
          const newItem = normalizeItems([updated])[0];
          setFaqs((prev) =>
            prev.map((f) => (Number(f.id) === Number(id) ? { ...f, ...newItem } : f))
          );
          return newItem;
        }
        await fetchFaqs();
        return true;
      } catch (err) {
        console.error("[FAQ] updateFaq error:", err);
        setError(err.message || "Failed to update FAQ");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [getHeaders, fetchFaqs]
  );


  // Delete FAQ
  const deleteFaq = useCallback(
    async (id) => {
      setLoading(true);
      setError(null);

      try {
        const deleteAuditId = localStorage.getItem("admin_id");

        const res = await fetch(`${BASE_URL}/faqs/${id}`, {
          method: "DELETE",
          headers: getHeaders(),
          body: JSON.stringify({ delete_audit_id: deleteAuditId }), // include audit id
        });

        const data = await res.json().catch(() => null);

        if (!res.ok) {
          const msg = data?.message || JSON.stringify(data) || `HTTP ${res.status}`;
          throw new Error(msg);
        }

        // Remove from local state
        setFaqs((prev) => prev.filter((f) => Number(f.id) !== Number(id)));
        return true;
      } catch (err) {
        console.error("[FAQ] deleteFaq error:", err);
        setError(err.message || "Failed to delete FAQ");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [getHeaders]
  );

  // Toggle status helper (convenience wrapper)
  const toggleStatus = useCallback(
    async (id) => {
      const target = faqs.find((f) => Number(f.id) === Number(id));
      if (!target) return false;
      const newStatus = target.status === "Active" ? "inActive" : "Active";
      const success = await updateFaq(id, { ...target, status: newStatus });
      return !!success;
    },
    [faqs, updateFaq]
  );

  // Auto-fetch when auth ready (and token exists)
  useEffect(() => {
    if (!isReady) return;
    if (!getToken()) {
    
      setFaqs([]);
      setLoading(false);
      setError(null);
      return;
    }
    const controller = new AbortController();
    fetchFaqs({ signal: controller.signal }).catch(() => {});
    return () => controller.abort();
  }, [isReady, adminId, getToken, fetchFaqs]);

  return (
    <FAQContext.Provider value={{ faqs, loading, error, fetchFaqs, createFaq, updateFaq, deleteFaq, toggleStatus }}>
      {children}
    </FAQContext.Provider>
  );
}

export function useFAQs() {
  return useContext(FAQContext);
}
